import type { Participant } from '#src/pages/create/types.ts';
import { exists } from '@packages/primitives/exists';
import { Button } from '@packages/ui/base/button/button';
import { Label } from '@packages/ui/base/input/label';
import { RadioGroup, RadioGroupItem } from '@packages/ui/base/radio_group/radio_group';
import { Multiselect } from '@packages/ui/base/select/multi_select';
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';

export interface AddExclusionsStepProps {
  participants: Participant[];
  wantsToAddExclusions: boolean;
  onWantsToAddExclusionsChange: (wantsToAddExclusions: boolean) => void;
  onUpdateParticipantExclusions: (participantId: string, exclusions: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export const AddExclusionsStep = ({
  participants,
  wantsToAddExclusions,
  onWantsToAddExclusionsChange,
  onUpdateParticipantExclusions,
  onNext,
  onBack,
}: AddExclusionsStepProps) => {
  const { errors: exclusionErrors, hasErrors: hasExclusionErrors } = getExclusionErrors(
    participants,
    wantsToAddExclusions,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Header */}

        <h1 className="mb-2 text-xl font-bold">Do you want to prevent specific matches?</h1>
        <div className="text-muted-foreground mb-4">
          Prevent certain people from matching together – like couples or family members.
        </div>

        {/* Exclusions Radio */}
        <div className="mb-8">
          <RadioGroup
            defaultValue={wantsToAddExclusions.toString()}
            onValueChange={value => onWantsToAddExclusionsChange(value === 'true')}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem id="wants-exclusions" value="true" />
              <Label htmlFor="wants-exclusions" className="text-base font-normal">
                Yes, prevent specific matches
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem id="not-wants-exclusions" value="false" />
              <Label htmlFor="not-wants-exclusions" className="text-base font-normal">
                No, allow any matches
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Exclusions List */}
        {wantsToAddExclusions && (
          <div className="mb-8 space-y-6">
            {participants.map(currentParticipant => {
              const currentExclusions = currentParticipant.exclusions;
              const possibleExclusions = participants
                .filter(p => p.id !== currentParticipant.id)
                .map(p => ({
                  label: p.name,
                  value: p.id,
                }));

              return (
                <div key={currentParticipant.id} className="flex flex-col gap-2">
                  <Label htmlFor={`exclusions-${currentParticipant.id}`} className="font-bold">
                    {currentParticipant.name} can't match with:
                  </Label>
                  <Multiselect
                    options={possibleExclusions}
                    values={currentExclusions}
                    placeholder="Select names..."
                    error={exclusionErrors[currentParticipant.id]}
                    onChange={newExclusions => {
                      onUpdateParticipantExclusions(currentParticipant.id, newExclusions);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 size-4" weight="bold" /> <span>Back</span>
        </Button>
        <Button className="w-full" onClick={onNext} disabled={hasExclusionErrors}>
          <span>Continue</span> <ArrowRightIcon className="ml-2 size-4" weight="bold" />
        </Button>
      </div>
    </div>
  );
};

/** Returns per-participant errors and whether any exist. Returns empty errors and false when
 * exclusions are disabled. */
export function getExclusionErrors(
  participants: Participant[],
  wantsToAddExclusions: boolean,
): { errors: Record<string, string | undefined>; hasErrors: boolean } {
  if (!wantsToAddExclusions) return { errors: {}, hasErrors: false };
  const possibleCount = participants.length - 1;
  const errors = Object.fromEntries(
    participants.map(p => {
      const isAllExcluded = possibleCount > 0 && p.exclusions.length === possibleCount;
      return [p.id, isAllExcluded ? "Can't exclude all possible matches." : undefined];
    }),
  );
  return { errors, hasErrors: Object.values(errors).some(exists) };
}
